import { useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Button } from '@carbon/react';
import { ChartArea } from '@carbon/react/icons';
import {
  crossSectionArmedAtom,
  disarmCrossSectionAtom,
  sectionModeMenuOpenAtom,
  startSectionAtom,
  toggleSectionModeMenuAtom,
} from './atoms';
import { SectionModeMenu } from './section-mode-menu';
import styles from './cross-section-arm-button.module.scss';

export interface CrossSectionArmButtonProps {
  /**
   * The control-stack button class, passed by whichever stack renders it, so the
   * button matches the column it sits in rather than carrying its own sizing.
   */
  className?: string;
  /** Called when the tool arms, so the host can put its own panels away. */
  onArm?: () => void;
}

/**
 * The map control-stack button that arms the Cross-Section tool, and the prompt it
 * opens beside itself.
 *
 * Arming asks which method to use, then puts the map into section mode. Pressing it
 * again while armed puts the tool away and takes the section with it — the button is
 * a toggle, not a one-way switch, which is what makes it the obvious way out as
 * well as in.
 *
 * @param {CrossSectionArmButtonProps} props - Component props.
 * @return {JSX.Element} The arm button, with the mode prompt when it is open.
 */
export const CrossSectionArmButton = ({
  className,
  onArm,
}: CrossSectionArmButtonProps): JSX.Element => {
  const armed = useAtomValue(crossSectionArmedAtom);
  const menuOpen = useAtomValue(sectionModeMenuOpenAtom);
  const toggleMenu = useSetAtom(toggleSectionModeMenuAtom);
  const startSection = useSetAtom(startSectionAtom);
  const disarm = useSetAtom(disarmCrossSectionAtom);
  const controlRef = useRef<HTMLDivElement>(null);

  return (
    <div className={styles.control} ref={controlRef}>
      <Button
        // Lit while armed, the way the stack's other toggles report themselves.
        kind={armed || menuOpen ? 'secondary' : 'ghost'}
        size="sm"
        hasIconOnly
        renderIcon={ChartArea}
        // Carbon draws the tooltip from the string it names the button with, so the two
        // cannot diverge. "Remove", not "close": this discards the drawn line, and
        // calling it a close would make that loss a surprise.
        iconDescription={
          armed ? 'Remove cross section' : 'Define cross section'
        }
        tooltipAlignment="center"
        tooltipPosition="left"
        className={className}
        aria-pressed={armed || menuOpen}
        onClick={() => {
          // Once there is a section the button removes it, the same exit the
          // drawer's close gives: two ways out, one outcome.
          if (armed) {
            disarm();
            return;
          }
          toggleMenu();
          onArm?.();
        }}
      />
      {/* The prompt lives here and only here, whether it is the first section or the
          one after a Clear: the question is about what a map click will do, so it
          belongs beside the control that arms it with the map still in view. */}
      {menuOpen && (
        <SectionModeMenu
          anchorRef={controlRef}
          onPick={(picked) => startSection(picked)}
          onCancel={() => toggleMenu()}
        />
      )}
    </div>
  );
};
